import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Music, Search, X, Loader2, Check, Share2, ThumbsUp, Clock, Flame, TrendingUp, Bell, BellOff, Sun, Moon, CheckCircle, XCircle, Play, Users } from 'lucide-react';
import { LiveQR } from './components/LiveQR';
import { MomentoSubTabs } from './components/MomentoSubTabs';
import { guessBucketFromPeticion, getVisibleBuckets, countByBucket } from './utils/momentoBuckets';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from './config';

const API = API_URL;

// Generate unique voter ID
const getVoterId = () => {
  const stored = localStorage.getItem('voter_id');
  if (stored) return stored;
  const newId = `voter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('voter_id', newId);
  return newId;
};

// Request Status Card Component
const RequestStatusCard = ({ request, voted, onVote }) => {
  const getStatusInfo = () => {
    if (request.reproducida) {
      return { icon: CheckCircle, color: 'emerald', label: '¡Reproducida!', bg: 'bg-emerald-600/20 border-emerald-600/50' };
    }
    if (request.rechazada) {
      return { icon: XCircle, color: 'red', label: 'No disponible', bg: 'bg-red-600/20 border-red-600/50' };
    }
    if (request.estado === 'aprobada') {
      return { icon: Play, color: 'cyan', label: 'En cola', bg: 'bg-cyan-600/20 border-cyan-600/50' };
    }
    return { icon: Clock, color: 'amber', label: 'Pendiente', bg: 'bg-amber-600/20 border-amber-600/50' };
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`rounded-xl border p-4 ${status.bg}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-12 h-12 rounded-lg bg-${status.color}-600/30 flex items-center justify-center`}>
          <StatusIcon className={`w-6 h-6 text-${status.color}-400`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-white truncate">{request.titulo}</h4>
          <p className="text-slate-400 text-sm truncate">{request.artista}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`bg-${status.color}-600/30 text-${status.color}-400 border-${status.color}-600/50`}>
              {status.label}
            </Badge>
            {request.nombre_cliente && (
              <span className="text-xs text-slate-500">Por: {request.nombre_cliente}</span>
            )}
          </div>
        </div>

        {/* Vote button */}
        {!request.reproducida && !request.rechazada && (
          <button
            onClick={() => onVote(request.id)}
            disabled={voted}
            className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
              voted 
                ? 'bg-emerald-600/30 text-emerald-400' 
                : 'bg-slate-700 hover:bg-slate-600 text-white'
            }`}
          >
            <ThumbsUp className={`w-5 h-5 ${voted ? 'fill-current' : ''}`} />
            <span className="text-sm font-bold">{request.votos || 0}</span>
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Popular Song Card
const PopularSongCard = ({ song, position, onRequest }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${
      position === 1 ? 'bg-amber-500 text-black' :
      position === 2 ? 'bg-slate-400 text-black' :
      position === 3 ? 'bg-orange-700 text-white' :
      'bg-slate-700 text-slate-300'
    }`}>
      {position}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate">{song.titulo}</p>
      <p className="text-slate-400 text-sm truncate">{song.artista}</p>
    </div>
    <div className="flex items-center gap-1 text-emerald-400">
      <ThumbsUp className="w-4 h-4" />
      <span className="font-bold">{song.votos || 0}</span>
    </div>
  </div>
);

// Main Component
export const LiveGuestPortal2 = () => {
  const eventCode = window.location.pathname.split('/live/')[1];
  const [eventId, setEventId] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [spotifyResults, setSpotifyResults] = useState([]);
  const [searchingSpotify, setSearchingSpotify] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [requesterName, setRequesterName] = useState(localStorage.getItem('guest_name') || '');
  const [submitting, setSubmitting] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [votedSongs, setVotedSongs] = useState(new Set());
  const [myRequests, setMyRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('queue');
  const [darkMode, setDarkMode] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [connectedCount, setConnectedCount] = useState(0);
  const [momentoOverrides, setMomentoOverrides] = useState({});
  const [activeBucket, setActiveBucket] = useState('baile');
  
  const voterId = getVoterId();

  // Fetch event ID
  useEffect(() => {
    const fetchEventId = async () => {
      try {
        const response = await fetch(`${API}/eventos/by-code/${eventCode}`);
        if (!response.ok) throw new Error('Código inválido');
        const data = await response.json();
        setEventId(data.evento.id);
        setEventData(data.evento);
        if (data.evento.momento_tab_overrides) {
          setMomentoOverrides(data.evento.momento_tab_overrides);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Código de evento inválido');
        setLoading(false);
      }
    };
    
    if (eventCode) fetchEventId();
  }, [eventCode]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!eventId) return;
    
    try {
      const response = await fetch(`${API}/eventos/${eventId}/peticiones/live?estado=todas`);
      if (!response.ok) throw new Error('Error');
      
      const data = await response.json();
      const allRequests = data.peticiones || [];
      
      // Check for status changes on my requests
      const myRequestIds = JSON.parse(localStorage.getItem(`my_requests_${eventId}`) || '[]');
      const myReqs = allRequests.filter(r => myRequestIds.includes(r.id));
      
      // Notification for status changes
      myReqs.forEach(req => {
        const prevReq = myRequests.find(r => r.id === req.id);
        if (prevReq && notificationsEnabled) {
          if (!prevReq.reproducida && req.reproducida) {
            toast.success(`🎵 ¡"${req.titulo}" está sonando!`, { duration: 10000 });
            if (navigator.vibrate) navigator.vibrate([500, 200, 500]);
          } else if (!prevReq.estado?.includes('aprobada') && req.estado === 'aprobada') {
            toast.info(`✓ "${req.titulo}" fue aprobada y está en cola`);
          }
        }
      });
      
      setMyRequests(myReqs);
      setRequests(allRequests);
      // Refresca overrides desde el endpoint live (siguen la última edición del DJ)
      if (data.evento?.momento_tab_overrides) {
        setMomentoOverrides(data.evento.momento_tab_overrides);
      }
      
      // Track voted songs
      const voted = new Set();
      allRequests.forEach(req => {
        if (req.votantes?.includes(voterId)) voted.add(req.id);
      });
      setVotedSongs(voted);
      
      // Estimate connected guests
      const thirtyMinAgo = Date.now() - 30 * 60 * 1000;
      const recentVoters = new Set();
      allRequests.forEach(r => {
        if (new Date(r.fecha_peticion).getTime() > thirtyMinAgo) {
          (r.votantes || []).forEach(v => recentVoters.add(v));
        }
      });
      setConnectedCount(recentVoters.size);
      
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }, [eventId, voterId, myRequests, notificationsEnabled]);

  // Search Spotify
  const searchSpotify = useCallback(async (query) => {
    if (!query.trim()) {
      setSpotifyResults([]);
      return;
    }
    
    setSearchingSpotify(true);
    try {
      const response = await fetch(`${API}/spotify/public/search?query=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Error');
      const data = await response.json();
      setSpotifyResults(data.tracks || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchingSpotify(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) searchSpotify(searchTerm);
      else setSpotifyResults([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, searchSpotify]);

  // Vote handler
  const handleVote = async (requestId) => {
    if (votedSongs.has(requestId)) {
      toast.info('Ya votaste por esta canción');
      return;
    }
    
    try {
      const response = await fetch(`${API}/peticiones/${requestId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direccion: 'up', votante_id: voterId })
      });
      
      if (!response.ok) throw new Error('Error');
      const data = await response.json();
      
      if (data.success) {
        setVotedSongs(prev => new Set([...prev, requestId]));
        toast.success('¡Voto registrado!');
        fetchRequests();
      }
    } catch (error) {
      console.error('Vote error:', error);
      toast.error('Error al votar');
    }
  };

  // Add request handler
  const handleAddRequest = async () => {
    if (!selectedSong) return;
    
    // Save guest name
    if (requesterName) {
      localStorage.setItem('guest_name', requesterName);
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/eventos/${eventId}/peticiones/live`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo: selectedSong.name,
          artista: selectedSong.artists?.map(a => a.name).join(', ') || 'Desconocido',
          spotify_uri: selectedSong.uri,
          nombre_cliente: requesterName || 'Invitado',
          tipo: 'live'
        })
      });
      
      if (!response.ok) throw new Error('Error');
      const data = await response.json();
      
      if (data.success) {
        // Save to my requests
        const myRequestIds = JSON.parse(localStorage.getItem(`my_requests_${eventId}`) || '[]');
        myRequestIds.push(data.peticion.id);
        localStorage.setItem(`my_requests_${eventId}`, JSON.stringify(myRequestIds));
        
        toast.success('¡Canción añadida! Pídeles a tus amigos que voten');
        setShowAddDialog(false);
        setSelectedSong(null);
        setSearchTerm('');
        setSpotifyResults([]);
        fetchRequests();
        setActiveTab('mine'); // Switch to my requests tab
      }
    } catch (error) {
      console.error('Add error:', error);
      toast.error('Error al añadir');
    } finally {
      setSubmitting(false);
    }
  };

  // Request notification permission
  const requestNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notificaciones activadas');
      }
    }
  };

  // Auto-refresh
  useEffect(() => {
    if (!eventId) return;
    fetchRequests();
    const interval = setInterval(fetchRequests, 5000);
    return () => clearInterval(interval);
  }, [eventId, fetchRequests]);

  // Portal URL
  const portalUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Filter requests
  const pendingRequests = requests.filter(r => !r.rechazada && !r.reproducida);
  const topVoted = [...requests].sort((a, b) => (b.votos || 0) - (a.votos || 0)).slice(0, 5);

  // Buckets visibles según overrides del DJ (baile siempre visible; el resto
  // se muestra si tiene peticiones o está forzado a mostrar).
  const bucketCounts = countByBucket(pendingRequests);
  const visibleBuckets = getVisibleBuckets(bucketCounts, momentoOverrides);
  // Filtrado por bucket activo
  const filteredPending = pendingRequests.filter(
    (r) => guessBucketFromPeticion(r) === activeBucket
  );
  // Asegura que el activeBucket sea siempre uno visible (si el DJ lo ocultó, saltamos a baile)
  useEffect(() => {
    if (!visibleBuckets.includes(activeBucket)) {
      setActiveBucket('baile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBuckets.join('|')]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-slate-950' : 'bg-slate-100'}`}>
        <Loader2 className={`h-10 w-10 animate-spin ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900'}`}>
      {/* Header */}
      <div className={`relative overflow-hidden ${darkMode ? 'bg-gradient-to-b from-emerald-900/20 to-transparent' : 'bg-gradient-to-b from-emerald-100 to-transparent'}`}>
        <div className="container max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {eventData?.nombre_novios || 'Peticiones'}
              </h1>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Pide tu canción favorita
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Connected users */}
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-white text-slate-600'}`}>
                <Users className="w-3 h-3" />
                {connectedCount}
              </div>
              
              {/* Notifications toggle */}
              <Button
                onClick={notificationsEnabled ? () => setNotificationsEnabled(false) : requestNotifications}
                variant="ghost"
                size="sm"
                className={`rounded-full ${notificationsEnabled ? 'text-emerald-400' : ''}`}
              >
                {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </Button>
              
              {/* Theme toggle */}
              <Button
                onClick={() => setDarkMode(!darkMode)}
                variant="ghost"
                size="sm"
                className="rounded-full"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-2xl mx-auto px-4 pb-24">
        {/* Search Card */}
        <Card className={`mb-6 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input
                type="search"
                placeholder="Buscar canción o artista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 h-12 text-base ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}`}
              />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setSpotifyResults([]); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Search results */}
            {searchingSpotify && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Buscando...</span>
              </div>
            )}

            <AnimatePresence>
              {spotifyResults.length > 0 && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 space-y-2 max-h-80 overflow-y-auto"
                >
                  {spotifyResults.map((song) => (
                    <button
                      key={song.id}
                      onClick={() => { setSelectedSong(song); setShowAddDialog(true); }}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        darkMode 
                          ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-700' 
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {song.album?.images?.[2]?.url && (
                          <img src={song.album.images[2].url} alt="" className="w-12 h-12 rounded object-cover" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">{song.name}</p>
                          <p className={`truncate text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {song.artists?.map(a => a.name).join(', ')}
                          </p>
                        </div>
                        <Music className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className={`grid grid-cols-3 ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
            <TabsTrigger value="queue" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
              Cola ({filteredPending.length})
            </TabsTrigger>
            <TabsTrigger value="mine" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              Mis Pedidos ({myRequests.length})
            </TabsTrigger>
            <TabsTrigger value="top" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
              <Flame className="w-4 h-4 mr-1" />
              Top 5
            </TabsTrigger>
          </TabsList>

          {/* Queue Tab */}
          <TabsContent value="queue" className="mt-4 space-y-3">
            {/* Subpestañas por momento (Ceremonia / Cóctel / Banquete / Baile).
                Sólo muestra las que el DJ tiene visibles en el evento. */}
            <div className="mb-2">
              <MomentoSubTabs
                peticiones={pendingRequests}
                activeBucket={activeBucket}
                onChange={setActiveBucket}
                overrides={momentoOverrides}
                readOnly={true}
                className="justify-start"
              />
            </div>
            {filteredPending.length === 0 ? (
              <Card className={darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="p-8 text-center">
                  <Music className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>No hay peticiones en este momento</p>
                  <p className={`text-sm mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    ¡Sé el primero en pedir una canción!
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredPending.map((request) => (
                <RequestStatusCard
                  key={request.id}
                  request={request}
                  voted={votedSongs.has(request.id)}
                  onVote={handleVote}
                />
              ))
            )}
          </TabsContent>

          {/* My Requests Tab */}
          <TabsContent value="mine" className="mt-4 space-y-3">
            {myRequests.length === 0 ? (
              <Card className={darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="p-8 text-center">
                  <Music className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>No has pedido ninguna canción</p>
                  <p className={`text-sm mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Busca arriba para pedir tu favorita
                  </p>
                </CardContent>
              </Card>
            ) : (
              myRequests.map((request) => (
                <RequestStatusCard
                  key={request.id}
                  request={request}
                  voted={votedSongs.has(request.id)}
                  onVote={handleVote}
                />
              ))
            )}
          </TabsContent>

          {/* Top 5 Tab */}
          <TabsContent value="top" className="mt-4 space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold">Más Votadas</h3>
            </div>
            {topVoted.length === 0 ? (
              <Card className={darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="p-8 text-center">
                  <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>No hay votos aún</p>
                </CardContent>
              </Card>
            ) : (
              topVoted.map((song, idx) => (
                <PopularSongCard key={song.id} song={song} position={idx + 1} />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom Bar */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 border-t safe-bottom ${
        darkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-slate-200'
      } backdrop-blur`}>
        <div className="container max-w-2xl mx-auto flex items-center justify-between gap-3">
          <Button
            onClick={() => setShowQRDialog(true)}
            variant="outline"
            size="sm"
            className={darkMode ? 'border-slate-700' : ''}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Compartir
          </Button>
          <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Actualizando en tiempo real
          </div>
        </div>
      </div>

      {/* Add Request Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className={darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Agregar Petición</DialogTitle>
            <DialogDescription className={darkMode ? 'text-slate-400' : ''}>
              ¡Pídeles a tus amigos que voten para que suene antes!
            </DialogDescription>
          </DialogHeader>

          {selectedSong && (
            <div className="space-y-4">
              <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                <p className="font-semibold">{selectedSong.name}</p>
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {selectedSong.artists?.map(a => a.name).join(', ')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Tu nombre (opcional)</label>
                <Input
                  placeholder="Ej: María"
                  value={requesterName}
                  onChange={(e) => setRequesterName(e.target.value)}
                  maxLength={50}
                  className={darkMode ? 'bg-slate-700 border-slate-600' : ''}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowAddDialog(false); setSelectedSong(null); }}
              disabled={submitting}
              className={darkMode ? 'border-slate-600' : ''}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddRequest}
              disabled={submitting || !selectedSong}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Añadiendo...</>
              ) : (
                <><Check className="w-4 h-4 mr-2" /> Agregar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className={darkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}>
          <DialogHeader>
            <DialogTitle>Comparte con tus amigos</DialogTitle>
            <DialogDescription className={darkMode ? 'text-slate-400' : ''}>
              Que voten por tu canción favorita
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <LiveQR url={portalUrl} size={200} caption="" />
          </div>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(portalUrl);
              toast.success('URL copiada');
            }}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            Copiar URL
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveGuestPortal2;
